// /src/backendServices/adminManagment/api_addressBook.ts

import   axios                from 'axios';

import { BackendResponse    ,
         handleError         } from '../../dataTypes/backendServicesManagment/types';

import { AddressBookContact ,
         initializedAddressBookContact } from '../../dataTypes/adminManagment/types_addressBook';

import { API_URLS            } from '../urls';

// ============================================================
// Types
// ============================================================

export interface BackendResponse_AddressBookContact     extends BackendResponse<AddressBookContact>   {}
export interface BackendResponse_AddressBookContactList extends BackendResponse<AddressBookContact[]> {}

// ============================================================
// Mapping
// ============================================================

const mapRawContact = (rawContact: any): AddressBookContact => {

    const defaultContact = initializedAddressBookContact();

    return {
        ...defaultContact,
        entityId    : rawContact.id          ?? defaultContact.entityId   ,
        contactType : rawContact.contactType ?? defaultContact.contactType,
        firstname   : rawContact.firstname   ?? defaultContact.firstname  ,
        lastname    : rawContact.lastname    ?? defaultContact.lastname   ,
        email       : rawContact.email       ?? defaultContact.email      ,
        whatsapp    : rawContact.whatsapp    ?? defaultContact.whatsapp   ,
    };
};

const jsonHeaders = {
    'Content-Type': 'application/json',
};

// ============================================================
// Récupération du carnet d'adresses d'un admin
// ============================================================

export const getAddressBook = async (adminId: number): Promise<BackendResponse_AddressBookContactList> => {

    const url = API_URLS.getAddressBook.replace('{id}', `${adminId}`);

    console.log('getAddressBook', url);

    try
    {
        const response = await axios.get(url);

        console.log('getAddressBook', response);

        if (response.status === 200)
        {
            const contacts: AddressBookContact[] = response.data['hydra:member'].map((rawContact: any) => mapRawContact(rawContact));

            return {
                code    : 1,
                comment : 'La récupération du carnet d\'adresses est terminée.',
                payload : contacts,
            };
        }
        else
        {
            return handleError<AddressBookContact[]>(response.status, 'Réponse non-200 de l\'API');
        }
    }
    catch (error)
    {
        if (axios.isAxiosError(error))
        {
            return handleError<AddressBookContact[]>(-101, error.response?.data?.detail ?? error.message);
        }

        return handleError<AddressBookContact[]>(-102, 'Erreur inconnue');
    }
};

// ============================================================
// Ajout d'un contact dans le carnet d'adresses
// ============================================================

export const addAddressBookContact = async ( adminId     : number     ,
                                             contactType : string     ,
                                             firstname   : string     ,
                                             lastname    : string     ,
                                             email       : string     ,
                                             whatsapp    : string = '' ): Promise<BackendResponse_AddressBookContact> => {

    const url = API_URLS.addAddressBookContact.replace('{id}', `${adminId}`);

    console.log('addAddressBookContact', url);

    const body = {
        contactType ,
        firstname   ,
        lastname    ,
        email       ,
        whatsapp    ,
    };

    try
    {
        const response = await axios.post(url, body, { headers: jsonHeaders });

        console.log('addAddressBookContact', response);

        if (response.status === 200 || response.status === 201)
        {
            return {
                code    : 1,
                comment : 'Le contact a été ajouté au carnet d\'adresses.',
                payload : mapRawContact(response.data),
            };
        }
        else
        {
            return handleError<AddressBookContact>(response.status, 'Réponse non-200 de l\'API');
        }
    }
    catch (error)
    {
        if (axios.isAxiosError(error))
        {
            return handleError<AddressBookContact>(-101, error.response?.data?.detail ?? error.message);
        }

        return handleError<AddressBookContact>(-102, 'Erreur inconnue');
    }
};

// ============================================================
// Modification d'un contact du carnet d'adresses
// ============================================================

export const updateAddressBookContact = async ( adminId   : number     ,
                                                entityId  : number     ,
                                                firstname : string     ,
                                                lastname  : string     ,
                                                email     : string     ,
                                                whatsapp  : string = '' ): Promise<BackendResponse_AddressBookContact> => {

    const url = API_URLS.updateAddressBookContact.replace('{id}', `${adminId}`);

    console.log('updateAddressBookContact', url);

    // Le serveur distingue trois cas par canal : champ absent du corps (inchangé),
    // champ présent et vide (coordonnée effacée), champ renseigné (créé ou modifié).
    // L'écran d'édition envoie toujours les quatre champs : ce qu'il montre vide
    // doit donc s'effacer, et c'est bien ce qu'on veut.
    const body = {
        entityId  ,
        firstname ,
        lastname  ,
        email     ,
        whatsapp  ,
    };

    try
    {
        const response = await axios.post(url, body, { headers: jsonHeaders });

        console.log('updateAddressBookContact', response);

        if (response.status === 200 || response.status === 201)
        {
            return {
                code    : 1,
                comment : 'Le contact a été modifié.',
                payload : mapRawContact(response.data),
            };
        }
        else
        {
            return handleError<AddressBookContact>(response.status, 'Réponse non-200 de l\'API');
        }
    }
    catch (error)
    {
        if (axios.isAxiosError(error))
        {
            return handleError<AddressBookContact>(-101, error.response?.data?.detail ?? error.message);
        }

        return handleError<AddressBookContact>(-102, 'Erreur inconnue');
    }
};

// ============================================================
// Suppression d'un contact du carnet d'adresses
// ============================================================

export const deleteAddressBookContact = async ( adminId  : number ,
                                                entityId : number ): Promise<BackendResponse<void>> => {

    const url = API_URLS.deleteAddressBookContact.replace('{id}', `${adminId}`);

    console.log('deleteAddressBookContact', url);

    const body = {
        entityId ,
    };

    try
    {
        const response = await axios.post(url, body, { headers: jsonHeaders });

        console.log('deleteAddressBookContact', response);

        if (response.status === 200 || response.status === 201)
        {
            return {
                code    : 1,
                comment : 'Le contact a été supprimé du carnet d\'adresses.',
                payload : undefined,
            };
        }
        else
        {
            return handleError<void>(response.status, 'Réponse non-200 de l\'API');
        }
    }
    catch (error)
    {
        if (axios.isAxiosError(error))
        {
            return handleError<void>(-101, error.response?.data?.detail ?? error.message);
        }

        return handleError<void>(-102, 'Erreur inconnue');
    }
};